// -- External libraries
import {
    Show,
    splitProps,
} from 'solid-js'
import { Portal } from 'solid-js/web'
import { useIntl } from '@cookbook/solid-intl'

import { useModalOrganismComponentController } from './modal-organism-component-controller.js'
import './modal-organism-component.scss'
import ButtonAtomComponent from '../../atoms/button-atom/button-atom-component.jsx'
import CardAtomComponent from '../../atoms/card-atom/card-atom-component.jsx'
import CustomSVG from '../../atoms/custom-svg.jsx'
import { messages } from './modal-organism-component.messages.en.js'
import { messages as sharedMessages } from '../../../messages/shared.messages.en.js'

export default function ModalOrganismComponent (props) {
    // `useIntl` and `messages` were imported for the back button's aria-label
    // fallback but never wired up, so any caller passing `backAction` without
    // `backLabel` threw "intl is not defined". Pre-existing on dev.
    const intl = useIntl()
    const {
        mergedProps,
        setModalReference,
        titleId,
    } = useModalOrganismComponentController(props)
    const requiredAttributes = [
        'title',
        'backAction',
        'backLabel',
        'closeLabel',
        'header',
        'footer',
        'onClose',
        'noClose',
        'seamlessSections',
        'fullHeight',
        'trapFocus',
        'restoreFocus',
    ]
    const [
        requestAttributes,
        styles,
        attributes,
    ] = splitProps(mergedProps, requiredAttributes, [
        'class',
        'classList',
    ])

    const rootClasses = ['modal-organism-component']
    if (styles.class) rootClasses.push(styles.class)

    return (
        <Portal {...attributes}>
            <div class={rootClasses.join(' ')} classList={styles.classList}>
                <div
                    class="modal-organism-component__backdrop"
                    onClick={() => {
                        !requestAttributes.noClose && requestAttributes.onClose()
                    }}
                >
                    {/* `role`/`aria-modal`/`aria-labelledby` are unconditional: they
                        change no pixel and no behaviour, and without them every
                        modal in the app is an anonymous div to a screen reader.
                        The label points at the title only when there IS one — a
                        `header` slot leaves that element empty. */}
                    <CardAtomComponent
                        tabIndex="-1"
                        ref={setModalReference}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={requestAttributes.title ? titleId : undefined}
                        class="modal-organism-component__card"
                        classList={{
                            'modal-organism-component__card--full-height': requestAttributes.fullHeight,
                        }}
                        shadow={true}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Show when={requestAttributes.header || requestAttributes.title}>
                            <div
                                class='modal-organism-component__card__header'
                                classList={{
                                    'modal-organism-component__card__header--seamless': requestAttributes.seamlessSections,
                                    'modal-organism-component__card__header--with-back': requestAttributes.backAction,
                                }}
                            >
                                <Show when={requestAttributes.header}>
                                    {requestAttributes.header}
                                </Show>
                                <Show when={!requestAttributes.header && requestAttributes.backAction}>
                                    <ButtonAtomComponent
                                        class="modal-organism-component__card__header__back-button"
                                        tabIndex="0"
                                        aria-label={requestAttributes.backLabel || intl.formatMessage(messages.backAriaLabel)}
                                        onClick={requestAttributes.backAction}
                                    >
                                        <CustomSVG path="icons/arrow-tail-left" />
                                    </ButtonAtomComponent>
                                </Show>
                                <div
                                    id={titleId}
                                    class="modal-organism-component__card__header__title"
                                >
                                    {requestAttributes.title}
                                </div>
                                <Show when={!requestAttributes.noClose}>
                                    {/* The close control was an unlabelled <button>
                                        wrapping a bare <path> — nothing announced
                                        it. The default comes from the shared
                                        catalog, so no caller has to pass one and
                                        no new string needed translating. */}
                                    <ButtonAtomComponent
                                        class="modal-organism-component__card__header__close-button"
                                        tabIndex="0"
                                        aria-label={requestAttributes.closeLabel || intl.formatMessage(sharedMessages.buttonClose)}
                                        onClick={() => requestAttributes.onClose()}
                                    >
                                        <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M11.609 0.295245C11.2184 -0.0952796 10.5922 -0.102233 10.1927 0.297316L5.9521 4.53789L1.71153 0.297316C1.34999 -0.0642238 0.784207 -0.0951447 0.390004 0.211537L0.295245 0.295245C-0.0952796 0.685769 -0.102233 1.31198 0.297316 1.71153L4.53789 5.9521L0.297316 10.1927C-0.0642238 10.5542 -0.0951447 11.12 0.211537 11.5142L0.295245 11.609C0.685769 11.9995 1.31198 12.0064 1.71153 11.6069L5.9521 7.36631L10.1927 11.6069C10.5542 11.9684 11.12 11.9993 11.5142 11.6927L11.609 11.609C11.9995 11.2184 12.0064 10.5922 11.6069 10.1927L7.36631 5.9521L11.6069 1.71153C11.9684 1.34999 11.9993 0.784207 11.6927 0.390004L11.609 0.295245Z"
                                                fill="#343A40"
                                            />
                                        </svg>
                                    </ButtonAtomComponent>
                                </Show>
                            </div>
                        </Show>

                        <div
                            tabIndex="-1"
                            class={
                                'modal-organism-component__card__body'
                                + (requestAttributes.seamlessSections
                                    ? ' modal-organism-component__card__body--seamless'
                                    : '')
                            }
                        >
                            {attributes.children}
                        </div>
                        <Show when={requestAttributes.footer}>
                            <div
                                class={
                                    'modal-organism-component__card__footer'
                                    + (requestAttributes.seamlessSections
                                        ? ' modal-organism-component__card__footer--seamless'
                                        : '')
                                }
                            >
                                {requestAttributes.footer}
                            </div>
                        </Show>
                    </CardAtomComponent>
                </div>
            </div>
        </Portal>
    )
}
